/**
 * Meal Plan Store
 *
 * Zustand store for managing meal plan state including cache, loading states,
 * and meal editing operations.
 *
 * @module stores/mealPlanStore
 */

import { create } from 'zustand';
import { mealDomain } from '@/services/domains';
import type {
  MealPlanFrontend,
  EditMealFormData,
  AdjustMealFormData,
  SwapMealFormData,
  UpdateMealRequest,
  RegenerateMealRequest,
  SwapMealRequest,
} from '@/types/mealPlan';
import { toSnakeCase } from '@/utils/caseTransform';

/**
 * Meal plan store state interface
 */
interface MealPlanState {
  // Cache: mealPlanId -> meal plan data
  mealPlans: Record<string, MealPlanFrontend>;

  // Operation loading states
  operations: {
    updating: Record<string, boolean>; // mealPlanId-mealId -> boolean
    regenerating: Record<string, boolean>;
    swapping: Record<string, boolean>;
    fetching: Record<string, boolean>; // mealPlanId -> boolean
  };

  // Error states
  errors: Record<string, Error | null>;

  // UI state for dialogs
  dialogs: {
    editMeal: { isOpen: boolean; mealPlanId: string | null; mealId: string | null };
    adjustMeal: { isOpen: boolean; mealPlanId: string | null; mealId: string | null };
    swapMeal: { isOpen: boolean; mealPlanId: string | null; mealId: string | null };
  };

  // Actions
  fetchMealPlan: (mealPlanId: string) => Promise<void>;
  updateMeal: (mealPlanId: string, mealId: string, data: EditMealFormData) => Promise<void>;
  regenerateMeal: (mealPlanId: string, mealId: string, data: AdjustMealFormData) => Promise<void>;
  swapMeal: (mealPlanId: string, mealId: string, data: SwapMealFormData) => Promise<void>;

  // UI actions
  openEditDialog: (mealPlanId: string, mealId: string) => void;
  closeEditDialog: () => void;
  openAdjustDialog: (mealPlanId: string, mealId: string) => void;
  closeAdjustDialog: () => void;
  openSwapDialog: (mealPlanId: string, mealId: string) => void;
  closeSwapDialog: () => void;

  // Utility actions
  clearError: (key: string) => void;
  resetLoadingStates: () => void;
  reset: () => void;
}

/**
 * Initial state for the store
 */
const initialState = {
  mealPlans: {},
  operations: {
    updating: {},
    regenerating: {},
    swapping: {},
    fetching: {},
  },
  errors: {},
  dialogs: {
    editMeal: { isOpen: false, mealPlanId: null, mealId: null },
    adjustMeal: { isOpen: false, mealPlanId: null, mealId: null },
    swapMeal: { isOpen: false, mealPlanId: null, mealId: null },
  },
};

/**
 * Meal plan store using Zustand
 */
export const useMealPlanStore = create<MealPlanState>((set) => ({
  ...initialState,

  /**
   * Fetch meal plan from API and cache it
   */
  fetchMealPlan: async (mealPlanId: string) => {
    set((state) => ({
      operations: {
        ...state.operations,
        fetching: { ...state.operations.fetching, [mealPlanId]: true },
      },
      errors: { ...state.errors, [mealPlanId]: null },
    }));

    try {
      const mealPlan = await mealDomain.getMealPlan(mealPlanId);

      set((state) => ({
        mealPlans: { ...state.mealPlans, [mealPlanId]: mealPlan },
        operations: {
          ...state.operations,
          fetching: { ...state.operations.fetching, [mealPlanId]: false },
        },
      }));
    } catch (error) {
      set((state) => ({
        errors: { ...state.errors, [mealPlanId]: error as Error },
        operations: {
          ...state.operations,
          fetching: { ...state.operations.fetching, [mealPlanId]: false },
        },
      }));
      throw error;
    }
  },

  /**
   * Update meal with new data
   * Performs targeted cache patching without full refetch
   */
  updateMeal: async (mealPlanId: string, mealId: string, data: EditMealFormData) => {
    const operationKey = `${mealPlanId}-${mealId}`;

    set((state) => ({
      operations: {
        ...state.operations,
        updating: { ...state.operations.updating, [operationKey]: true },
      },
      errors: { ...state.errors, [operationKey]: null },
    }));

    try {
      // Transform to API format
      const requestData: UpdateMealRequest = toSnakeCase({
        mealName: data.mealName,
        nutrition: data.nutrition,
      });

      const updatedMeal = await mealDomain.updateMeal(mealPlanId, mealId, requestData);

      // Targeted cache patching - update only the affected meal
      set((state) => {
        const mealPlan = state.mealPlans[mealPlanId];
        if (!mealPlan) return state;

        return {
          mealPlans: {
            ...state.mealPlans,
            [mealPlanId]: {
              ...mealPlan,
              mealPlanData: {
                ...mealPlan.mealPlanData,
                meals: mealPlan.mealPlanData.meals.map((m) =>
                  m.id === mealId ? updatedMeal : m
                ),
              },
            },
          },
          operations: {
            ...state.operations,
            updating: { ...state.operations.updating, [operationKey]: false },
          },
        };
      });
    } catch (error) {
      set((state) => ({
        errors: { ...state.errors, [operationKey]: error as Error },
        operations: {
          ...state.operations,
          updating: { ...state.operations.updating, [operationKey]: false },
        },
      }));
      throw error;
    }
  },

  /**
   * Regenerate meal with AI guidance
   * Performs targeted cache patching without full refetch
   */
  regenerateMeal: async (mealPlanId: string, mealId: string, data: AdjustMealFormData) => {
    const operationKey = `${mealPlanId}-${mealId}`;

    set((state) => ({
      operations: {
        ...state.operations,
        regenerating: { ...state.operations.regenerating, [operationKey]: true },
      },
      errors: { ...state.errors, [operationKey]: null },
    }));

    try {
      // Transform to API format
      const requestData: RegenerateMealRequest = {
        guidance: data.guidance,
      };

      const regeneratedMeal = await mealDomain.regenerateMeal(mealPlanId, mealId, requestData);

      // Targeted cache patching - update only the affected meal
      set((state) => {
        const mealPlan = state.mealPlans[mealPlanId];
        if (!mealPlan) return state;

        return {
          mealPlans: {
            ...state.mealPlans,
            [mealPlanId]: {
              ...mealPlan,
              mealPlanData: {
                ...mealPlan.mealPlanData,
                meals: mealPlan.mealPlanData.meals.map((m, index) =>
                  index.toString() === mealId ? regeneratedMeal : m
                ),
              },
            },
          },
          operations: {
            ...state.operations,
            regenerating: { ...state.operations.regenerating, [operationKey]: false },
          },
        };
      });
    } catch (error) {
      set((state) => ({
        errors: { ...state.errors, [operationKey]: error as Error },
        operations: {
          ...state.operations,
          regenerating: { ...state.operations.regenerating, [operationKey]: false },
        },
      }));
      throw error;
    }
  },

  /**
   * Swap meal with new AI-generated meal
   * Updates entire meal plan cache as meal IDs may change
   */
  swapMeal: async (mealPlanId: string, mealId: string, data: SwapMealFormData) => {
    const operationKey = `${mealPlanId}-${mealId}`;

    set((state) => ({
      operations: {
        ...state.operations,
        swapping: { ...state.operations.swapping, [operationKey]: true },
      },
      errors: { ...state.errors, [operationKey]: null },
    }));

    try {
      // Transform to API format
      const requestData: SwapMealRequest = {
        guidance: data.specialInstructions,
        skip_evaluation: data.skipEvaluation ?? true,
      };

      const { mealPlan: updatedMealPlan } = await mealDomain.swapMeal(
        mealPlanId,
        mealId,
        requestData
      );

      // Update entire meal plan cache as meal IDs may have changed
      set((state) => ({
        mealPlans: {
          ...state.mealPlans,
          [mealPlanId]: updatedMealPlan,
        },
        operations: {
          ...state.operations,
          swapping: { ...state.operations.swapping, [operationKey]: false },
        },
      }));
    } catch (error) {
      set((state) => ({
        errors: { ...state.errors, [operationKey]: error as Error },
        operations: {
          ...state.operations,
          swapping: { ...state.operations.swapping, [operationKey]: false },
        },
      }));
      throw error;
    }
  },

  /**
   * Open edit meal dialog
   */
  openEditDialog: (mealPlanId: string, mealId: string) => {
    set((state) => ({
      dialogs: {
        ...state.dialogs,
        editMeal: { isOpen: true, mealPlanId, mealId },
      },
    }));
  },

  /**
   * Close edit meal dialog
   */
  closeEditDialog: () => {
    set((state) => ({
      dialogs: {
        ...state.dialogs,
        editMeal: { isOpen: false, mealPlanId: null, mealId: null },
      },
    }));
  },

  /**
   * Open adjust meal dialog
   */
  openAdjustDialog: (mealPlanId: string, mealId: string) => {
    set((state) => ({
      dialogs: {
        ...state.dialogs,
        adjustMeal: { isOpen: true, mealPlanId, mealId },
      },
    }));
  },

  /**
   * Close adjust meal dialog
   */
  closeAdjustDialog: () => {
    set((state) => ({
      dialogs: {
        ...state.dialogs,
        adjustMeal: { isOpen: false, mealPlanId: null, mealId: null },
      },
    }));
  },

  /**
   * Open swap meal dialog
   */
  openSwapDialog: (mealPlanId: string, mealId: string) => {
    set((state) => ({
      dialogs: {
        ...state.dialogs,
        swapMeal: { isOpen: true, mealPlanId, mealId },
      },
    }));
  },

  /**
   * Close swap meal dialog
   */
  closeSwapDialog: () => {
    set((state) => ({
      dialogs: {
        ...state.dialogs,
        swapMeal: { isOpen: false, mealPlanId: null, mealId: null },
      },
    }));
  },

  /**
   * Clear error for a specific key
   */
  clearError: (key: string) => {
    set((state) => ({
      errors: { ...state.errors, [key]: null },
    }));
  },

  /**
   * Reset all loading states to false
   * Useful after operations complete or when refreshing data
   */
  resetLoadingStates: () => {
    set({
      operations: {
        updating: {},
        regenerating: {},
        swapping: {},
        fetching: {},
      },
    });
  },

  /**
   * Reset store to initial state
   */
  reset: () => {
    set(initialState);
  },
}));